import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react/dist/iconify.js';
import Breadcrumb from '../../../components/Breadcrumb';
import { SOCIETY_ROLES_PERMISSIONS } from '../../../Routing/routes';
import {
    getSystemRoles,
    getSystemRoleDetails,
    getPermissionCatalog,
    createSystemRole,
    updateSystemRole,
    setSystemRolePermissions,
    deactivateSystemRole,
} from '../../../redux/actions/SocietyController/CtrlSystemRoleAction/CtrlSystemRoleAction';
import PermissionMatrix from './Component/PermissionMatrix';
import RoleFormModal from './Component/RoleFormModal';

const setsEqual = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));
const isActive = (r) => r?.is_active === 1 || r?.is_active === true;

const RolesPermissions = () => {
    const dispatch = useDispatch();
    const {
        rolesLoading, rolesData,
        detailsLoading, detailsData,
        catalogLoading, catalogData,
        setPermsLoading, deactivateLoading, createLoading, updateLoading,
    } = useSelector((s) => s.CtrlSystemRoleReducer || {});

    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [search, setSearch] = useState('');
    const [roleForm, setRoleForm] = useState({ open: false, mode: 'add', role: null });
    const [confirmDeactivate, setConfirmDeactivate] = useState(null);

    const roles = Array.isArray(rolesData) ? rolesData : [];
    const catalog = useMemo(() => (Array.isArray(catalogData) ? catalogData : []), [catalogData]);
    const allCatalogIds = useMemo(() => catalog.map((p) => p.permission_id), [catalog]);

    const selectedRole = roles.find((r) => r.role_id === selectedRoleId) || null;

    const fetchRoles = () => dispatch(getSystemRoles({ is_system_role: 1, limit: 100 }));

    useEffect(() => {
        fetchRoles();
        dispatch(getPermissionCatalog({ all_modules: 1, limit: 500 }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Auto-select the first role once loaded.
    useEffect(() => {
        if (!selectedRoleId && roles.length) setSelectedRoleId(roles[0].role_id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roles]);

    // Load the selected role's current permissions.
    useEffect(() => {
        if (selectedRoleId) dispatch(getSystemRoleDetails({ id: selectedRoleId }));
    }, [selectedRoleId, dispatch]);

    // Saved permission ids for the currently selected role.
    const savedIds = useMemo(() => {
        if (detailsData && detailsData.role_id === selectedRoleId) {
            return new Set((detailsData.permissions || []).map((p) => p.permission_id));
        }
        return new Set();
    }, [detailsData, selectedRoleId]);

    // Pre-check boxes when a role's details arrive.
    useEffect(() => {
        if (detailsData && detailsData.role_id === selectedRoleId) {
            setSelectedIds(new Set((detailsData.permissions || []).map((p) => p.permission_id)));
        }
    }, [detailsData, selectedRoleId]);

    const dirty = !setsEqual(selectedIds, savedIds);

    const toggle = (id) => setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const toggleMany = (ids) => {
        const allSel = ids.every((id) => selectedIds.has(id));
        setSelectedIds((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => (allSel ? next.delete(id) : next.add(id)));
            return next;
        });
    };
    const selectAll = () => setSelectedIds(new Set(allCatalogIds));
    const clearAll = () => setSelectedIds(new Set());
    const resetToSaved = () => setSelectedIds(new Set(savedIds));

    const handleSave = () => {
        if (!selectedRoleId) return;
        dispatch(setSystemRolePermissions({
            id: selectedRoleId,
            permission_ids: Array.from(selectedIds),
            onSuccess: () => dispatch(getSystemRoleDetails({ id: selectedRoleId })),
        }));
    };

    const handleRoleFormSubmit = (payload, { isEdit }) => {
        const action = isEdit ? updateSystemRole : createSystemRole;
        dispatch(action({
            ...payload,
            onSuccess: (res) => {
                setRoleForm({ open: false, mode: 'add', role: null });
                fetchRoles();
                const newId = res?.data?.role_id;
                if (!isEdit && newId) setSelectedRoleId(newId);
            },
        }));
    };

    const handleDeactivate = () => {
        if (!confirmDeactivate) return;
        dispatch(deactivateSystemRole({
            id: confirmDeactivate.role_id,
            onSuccess: () => {
                setConfirmDeactivate(null);
                fetchRoles();
                if (selectedRoleId === confirmDeactivate.role_id) dispatch(getSystemRoleDetails({ id: selectedRoleId }));
            },
        }));
    };

    const links = [{ to: SOCIETY_ROLES_PERMISSIONS, text: 'Roles & Permissions' }];
    const totalSelected = selectedIds.size;
    const isSuperAdmin = selectedRole?.role_code === 'SUPER_ADMIN';

    return (
        <>
            <Breadcrumb
                replaceClass="mb-24"
                description="Manage default system roles and their permissions"
                links={links}
                headingTitle="Roles & Permissions"
            />

            {/* Global-impact warning */}
            <div className="alert alert-warning bg-warning-50 border-warning-200 text-warning-700 d-flex align-items-start gap-2 radius-8 px-16 py-12 mb-16">
                <Icon icon="solar:danger-triangle-bold" className="text-warning-600 text-xl mt-1 flex-shrink-0" />
                <div className="text-sm">
                    <strong>System roles are global.</strong> Changing a role's permissions takes effect immediately
                    for every user with that role, in every society, and for newly created societies. Every change is audit-logged.
                </div>
            </div>

            <div className="row gy-3">
                {/* Left — roles list */}
                <div className="col-lg-3">
                    <div className="card h-100 radius-12 border shadow-none">
                        <div className="card-header d-flex align-items-center justify-content-between py-12 px-16 border-bottom">
                            <span className="fw-semibold text-sm">Default Roles</span>
                            <button className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                onClick={() => setRoleForm({ open: true, mode: 'add', role: null })}>
                                <Icon icon="mdi:plus" /> Add
                            </button>
                        </div>
                        <div className="card-body p-8" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                            {rolesLoading ? (
                                <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary" /></div>
                            ) : roles.length === 0 ? (
                                <p className="text-center text-secondary-light text-sm py-4 mb-0">No roles found</p>
                            ) : (
                                roles.map((r) => {
                                    const active = r.role_id === selectedRoleId;
                                    return (
                                        <button key={r.role_id} onClick={() => setSelectedRoleId(r.role_id)}
                                            className={`w-100 text-start border-0 radius-8 px-12 py-10 mb-1 d-flex align-items-center gap-2 ${active ? 'bg-primary-50' : 'bg-transparent'}`}
                                            style={{ transition: 'background .15s' }}>
                                            <Icon icon="solar:shield-user-bold-duotone"
                                                className={active ? 'text-primary-600' : 'text-secondary-light'} />
                                            <span className="flex-grow-1 min-w-0">
                                                <span className={`d-block text-sm fw-medium text-truncate ${active ? 'text-primary-600' : ''}`}>
                                                    {r.role_name}
                                                </span>
                                                <span className="d-block text-xs text-secondary-light font-monospace text-truncate">
                                                    {r.role_code}
                                                </span>
                                            </span>
                                            {!isActive(r) && (
                                                <span className="badge bg-neutral-200 text-secondary-light text-xs">Off</span>
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Right — permission matrix for the selected role */}
                <div className="col-lg-9">
                    <div className="card h-100 radius-12 border shadow-none">
                        {!selectedRole ? (
                            <div className="card-body text-center py-5 text-secondary-light">
                                <Icon icon="solar:shield-keyhole-linear" className="text-4xl mb-2 d-block mx-auto" />
                                <p className="text-sm mb-0">Select a role to manage its permissions.</p>
                            </div>
                        ) : (
                            <>
                                <div className="card-header py-12 px-16 border-bottom">
                                    <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                                        <div className="min-w-0">
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                <h6 className="mb-0 fw-semibold">{selectedRole.role_name}</h6>
                                                <span className="badge bg-neutral-200 text-secondary-light font-monospace text-xs">{selectedRole.role_code}</span>
                                                {!isActive(selectedRole) && <span className="badge bg-danger-focus text-danger-main text-xs">Inactive</span>}
                                                <span className="text-secondary-light text-sm">· {totalSelected}/{allCatalogIds.length} permissions</span>
                                                {dirty && <span className="badge bg-warning-focus text-warning-main text-xs">Unsaved</span>}
                                            </div>
                                            {selectedRole.description && (
                                                <p className="text-xs text-secondary-light mb-0 mt-1">{selectedRole.description}</p>
                                            )}
                                        </div>
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                                onClick={() => setRoleForm({ open: true, mode: 'edit', role: selectedRole })}>
                                                <Icon icon="lucide:edit" /> Edit
                                            </button>
                                            <button className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                                onClick={() => setConfirmDeactivate(selectedRole)}
                                                disabled={isSuperAdmin || !isActive(selectedRole)}
                                                title={isSuperAdmin ? 'SUPER_ADMIN cannot be deactivated' : undefined}>
                                                <Icon icon="solar:forbidden-circle-linear" /> Deactivate
                                            </button>
                                        </div>
                                    </div>

                                    {/* Matrix toolbar */}
                                    <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                                        <div className="icon-field">
                                            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                                                placeholder="Search permissions" className="form-control form-control-sm w-auto" style={{ minWidth: 200 }} />
                                            <span className="icon"><Icon icon="ion:search-outline" /></span>
                                        </div>
                                        <button className="btn btn-sm btn-outline-primary" onClick={selectAll}
                                            disabled={totalSelected === allCatalogIds.length}>Select All</button>
                                        <button className="btn btn-sm btn-outline-secondary" onClick={clearAll}
                                            disabled={totalSelected === 0}>Clear</button>
                                        <button className="btn btn-sm btn-outline-secondary" onClick={resetToSaved}
                                            disabled={!dirty}>Reset</button>
                                        <button className="btn btn-sm btn-primary d-flex align-items-center gap-1 ms-auto"
                                            onClick={handleSave} disabled={!dirty || setPermsLoading}>
                                            {setPermsLoading ? <span className="spinner-border spinner-border-sm" /> : <Icon icon="solar:diskette-bold" />}
                                            Save Permissions
                                        </button>
                                    </div>
                                </div>

                                <div className="card-body p-16" style={{ maxHeight: '62vh', overflowY: 'auto' }}>
                                    {(detailsLoading || catalogLoading) ? (
                                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
                                    ) : (
                                        <PermissionMatrix
                                            catalog={catalog}
                                            selectedIds={selectedIds}
                                            onToggle={toggle}
                                            onToggleMany={toggleMany}
                                            search={search}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <RoleFormModal
                open={roleForm.open}
                mode={roleForm.mode}
                role={roleForm.role}
                submitting={createLoading || updateLoading}
                onClose={() => setRoleForm({ open: false, mode: 'add', role: null })}
                onSubmit={handleRoleFormSubmit}
            />

            {/* Deactivate confirm */}
            {confirmDeactivate && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.4)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content radius-12 border-0">
                            <div className="modal-body p-24 text-center">
                                <Icon icon="solar:forbidden-circle-bold" className="text-danger-main" style={{ fontSize: 52 }} />
                                <h6 className="fw-semibold mt-2 mb-1">Deactivate “{confirmDeactivate.role_name}”?</h6>
                                <p className="text-secondary-light text-sm mb-0">
                                    The role becomes inactive. In-use roles can't be deactivated until users are reassigned.
                                    You can reactivate it later via Edit.
                                </p>
                            </div>
                            <div className="modal-footer border-0 justify-content-center pb-24 gap-2">
                                <button className="btn btn-outline-secondary btn-sm px-20" onClick={() => setConfirmDeactivate(null)} disabled={deactivateLoading}>Cancel</button>
                                <button className="btn btn-danger btn-sm px-20 d-flex align-items-center gap-1" onClick={handleDeactivate} disabled={deactivateLoading}>
                                    {deactivateLoading && <span className="spinner-border spinner-border-sm" />} Deactivate
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default RolesPermissions;
